import { DataFormats } from '@skylab-shared/constants';
import { HttpService, Query, QueryHandler, RootContextBase, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { PurchaseDocumentKind } from '@skylab-shared/dataModel';
import { MS_PER_MINUTE, StringUtils } from '@skylab-shared/utils';
import { Transform } from 'class-transformer';
import { format } from 'fecha';
import { CountryCollectionQuery } from '../../country/queries/country.query-base';
import { PaymentMethodQuery } from '../../payment-method/queries/payment-method.query-base';

export interface PurchasesByPeriodParameters {
    issueDateFrom: Date;
    issueDateTo: Date;
    dueDateFrom: Date;
    dueDateTo: Date;
    deliveryDateFrom: Date;
    deliveryDateTo: Date;
    taxLiabilityDateFrom: Date;
    taxLiabilityDateTo: Date;
    clientGroupId?: string;
    noClientGroup?: boolean;
    search: string;
    paymentMethodId?: number;
    kinds: (PurchaseDocumentKind)[];
}

export interface PurchasesByPeriodModel {
    baseCurrencyGross: number;
    baseCurrency: string;
    baseCurrencyPrecision: number;
    baseCurrencyNet: number;
    baseCurrencyVat: number;
    sellerName: string;
    sellerAddress: string | null;
    clientGroup: string;
    sellerCountry?: string;
    clientGroupId: string;
    currency: string;
    currencyPrecision: number;
    documentId: number;
    documentKind: PurchaseDocumentKind;
    documentNumber: string;
    externalDocumentNumber: string;
    expenseName: string;
    gross: number;
    issueDate: Date;
    dueDate: Date;
    deliveryDate: Date;
    taxLiabilityDate: Date;
    net: number;
    sellerTin: string;
    vat: number;
    paymentMethodName?: string;
}

class PurchasesByPeriodInternalItemDTO {
    public readonly baseCurrency!: string;
    public readonly baseCurrencyGross!: number;
    public readonly baseCurrencyNet!: number;
    public readonly baseCurrencyPrecision!: number;
    public readonly baseCurrencyVat!: number;
    public readonly clientGroup!: string;
    public readonly clientGroupId!: string;
    public readonly currency!: string;
    public readonly currencyPrecision!: number;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly deliveryDate!: Date;
    public readonly documentId!: number;
    public readonly documentKind!: PurchaseDocumentKind;
    public readonly documentNumber!: string;
    public readonly expenseName!: string;
    public readonly externalDocumentNumber!: string;
    public readonly gross!: number;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly issueDate!: Date;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly dueDate!: Date;
    public readonly net!: number;
    public readonly paymentMethodId!: number;
    public readonly sellerAddress!: {
        address: string;
        city: string;
        countryId: number;
        name: string;
        zipCode: string;
    };
    public readonly sellerName!: string;
    public readonly sellerTin!: string;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly taxLiabilityDate!: Date;
    public readonly vat!: number;
}

export class PurchasesByPeriodCollectionQuery extends Query<PurchasesByPeriodModel[]> {
    public constructor(public readonly parameters: PurchasesByPeriodParameters) {
        super();
    }
}

export class PurchasesByPeriodCollectionQueryHandlerBase implements QueryHandler<PurchasesByPeriodCollectionQuery, PurchasesByPeriodModel[]> {
    private static Url = TaggedUrl.join('purchaseByPeriod', [Tags.Api]);

    public constructor(
        private readonly http: HttpService,
        private readonly rootContext: RootContextBase
    ) { }

    public async handleAsync(query: PurchasesByPeriodCollectionQuery): Promise<PurchasesByPeriodModel[]> {
        const { data } = await this.http.get<PurchasesByPeriodInternalItemDTO[]>(PurchasesByPeriodCollectionQueryHandlerBase.Url, {
            params: {
                ...query.parameters,
                issueDateFrom: query.parameters.issueDateFrom && format(query.parameters.issueDateFrom, DataFormats.date.webApi),
                issueDateTo: query.parameters.issueDateTo && format(query.parameters.issueDateTo, DataFormats.date.webApi),
                dueDateFrom: query.parameters.dueDateFrom && format(query.parameters.dueDateFrom, DataFormats.date.webApi),
                dueDateTo: query.parameters.dueDateTo && format(query.parameters.dueDateTo, DataFormats.date.webApi),
                deliveryDateFrom: query.parameters.deliveryDateFrom && format(query.parameters.deliveryDateFrom, DataFormats.date.webApi),
                deliveryDateTo: query.parameters.deliveryDateTo && format(query.parameters.deliveryDateTo, DataFormats.date.webApi),
                taxLiabilityDateFrom: query.parameters.taxLiabilityDateFrom && format(query.parameters.taxLiabilityDateFrom, DataFormats.date.webApi),
                taxLiabilityDateTo: query.parameters.taxLiabilityDateTo && format(query.parameters.taxLiabilityDateTo, DataFormats.date.webApi),
            },
            timeout: 3 * MS_PER_MINUTE,
            dto: PurchasesByPeriodInternalItemDTO
        });

        const paymentMethods = await this.rootContext.getAsync(PaymentMethodQuery);
        const countries = await this.rootContext.getAsync(CountryCollectionQuery);

        return data.map(item => ({
            baseCurrency: item.baseCurrency,
            baseCurrencyPrecision: item.baseCurrencyPrecision,
            baseCurrencyGross: item.baseCurrencyGross,
            baseCurrencyNet: item.baseCurrencyNet,
            baseCurrencyVat: item.baseCurrencyVat,
            sellerName: item.sellerName,
            sellerAddress: item.sellerAddress
                ? StringUtils.join(', ', item.sellerAddress.address, item.sellerAddress.zipCode, item.sellerAddress.city)
                : null,
            sellerCountry: countries.find(elem => elem.id === item.sellerAddress?.countryId)?.name,
            clientGroup: item.clientGroup,
            clientGroupId: item.clientGroupId,
            currency: item.currency,
            currencyPrecision: item.currencyPrecision,
            documentId: item.documentId,
            documentKind: item.documentKind,
            documentNumber: item.documentNumber,
            externalDocumentNumber: item.externalDocumentNumber,
            expenseName: item.expenseName,
            gross: item.gross,
            issueDate: item.issueDate,
            dueDate: item.dueDate,
            deliveryDate: item.deliveryDate,
            taxLiabilityDate: item.taxLiabilityDate,
            net: item.net,
            sellerTin: item.sellerTin,
            vat: item.vat,
            paymentMethodName: paymentMethods.find(elem => elem.id === item.paymentMethodId)?.name
        }));
    }
}
